import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Autocomplete from "@/components/Autocomplete";
import { searchStatic } from "@/lib/search-index";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Global ⌘K / Ctrl-K command palette. Renders as a full-screen overlay with
 * a large centred Autocomplete that searches products + services + pages +
 * gallery + blog posts. Closes on Escape, backdrop click, or after selecting
 * an item (Autocomplete navigates).
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const inputWrapRef = useRef(null);
  const navigate = useNavigate();

  // Global hotkey listener — ⌘K on macOS, Ctrl+K on Windows/Linux, or `/`.
  useEffect(() => {
    const onKey = (e) => {
      const isModK = (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      const isSlash = e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (isModK || isSlash) {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Prevent body scroll while open.
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      // Focus the input inside Autocomplete on next tick.
      setTimeout(() => inputWrapRef.current?.querySelector("input")?.focus(), 50);
    } else {
      document.body.style.overflow = "";
    }
  }, [open]);

  const runSearch = async (q) => {
    let productResults = [];
    try {
      const { data } = await axios.get(`${API}/search`, { params: { q, limit: 6 } });
      productResults = data.results || [];
    } catch { /* silent */ }
    const staticResults = searchStatic(q, { limit: 6 });
    const groups = [];
    if (productResults.length) groups.push({ label: "Print Shop", items: productResults });
    const grouped = { service: "Services", page: "Pages", blog: "Blog", gallery: "Gallery" };
    for (const [type, label] of Object.entries(grouped)) {
      const items = staticResults.filter((s) => s.type === type);
      if (items.length) groups.push({ label, items });
    }
    return { groups };
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-md flex items-start justify-center pt-[15vh] px-4"
      onClick={() => setOpen(false)}
      data-testid="command-palette"
    >
      <div
        className="w-full max-w-2xl relative"
        ref={inputWrapRef}
        onClick={(e) => e.stopPropagation()}
      >
        <Autocomplete
          variant="hero"
          testId="palette-search"
          placeholder="Search everything — products, services, pages…"
          onQuery={runSearch}
          onSelect={(item) => { setOpen(false); if (item.url) navigate(item.url); }}
          autoFocus
          noResultsHint="No matches. Try a product name like 'business cards' or a page like 'contact'."
          minQueryLength={1}
        />
        <div className="mt-3 flex justify-between items-center text-[10px] uppercase tracking-[0.22em] text-white/50 px-1">
          <div>
            <kbd className="bg-white/10 px-1.5 py-0.5 rounded font-mono">↑↓</kbd> to navigate ·{" "}
            <kbd className="bg-white/10 px-1.5 py-0.5 rounded font-mono">↵</kbd> to open ·{" "}
            <kbd className="bg-white/10 px-1.5 py-0.5 rounded font-mono">esc</kbd> to close
          </div>
          <div>
            <kbd className="bg-white/10 px-1.5 py-0.5 rounded font-mono">⌘K</kbd> anywhere
          </div>
        </div>
      </div>
    </div>
  );
}
